/**
 * Standard mathematical functions for expression evaluation
 *
 * These functions handle undefined inputs by returning undefined,
 * which propagates through the expression evaluation.
 */

export const standardFunctions: Record<string, (...args: (number | undefined)[]) => number | undefined> = {
  // Power and roots
  pow: (base: number | undefined, exponent: number | undefined) => base === undefined || exponent === undefined ? undefined : base ** exponent,

  sqrt: (x: number | undefined) => x === undefined ? undefined : Math.sqrt(x),

  // Rounding
  abs: (x: number | undefined) => x === undefined ? undefined : Math.abs(x),

  floor: (x: number | undefined) => x === undefined ? undefined : Math.floor(x),

  ceil: (x: number | undefined) => x === undefined ? undefined : Math.ceil(x),

  round: (x: number | undefined) => x === undefined ? undefined : Math.round(x),

  // Min/Max
  min: (...args: (number | undefined)[]) => {
    const filtered = args.filter((x): x is number => x != null);
    return filtered.length === 0 ? undefined : Math.min(...filtered);
  },

  max: (...args: (number | undefined)[]) => {
    const filtered = args.filter((x): x is number => x != null);
    return filtered.length === 0 ? undefined : Math.max(...filtered);
  },

  // Trigonometric
  sin: (x: number | undefined) => x === undefined ? undefined : Math.sin(x),

  cos: (x: number | undefined) => x === undefined ? undefined : Math.cos(x),

  tan: (x: number | undefined) => x === undefined ? undefined : Math.tan(x),

  asin: (x: number | undefined) => x === undefined ? undefined : Math.asin(x),

  acos: (x: number | undefined) => x === undefined ? undefined : Math.acos(x),

  atan: (x: number | undefined) => x === undefined ? undefined : Math.atan(x),

  atan2: (y: number | undefined, x: number | undefined) => y === undefined || x === undefined ? undefined : Math.atan2(y, x),

  // Logarithmic and exponential
  exp: (x: number | undefined) => x === undefined ? undefined : Math.exp(x),

  log: (x: number | undefined) => x === undefined ? undefined : Math.log(x),

  log10: (x: number | undefined) => x === undefined ? undefined : Math.log10(x),

  log2: (x: number | undefined) => x === undefined ? undefined : Math.log2(x),
};
